import React, { useState, useEffect } from 'react';
import { useFilePicker } from '../../hooks/useFilePicker';
import { useParams, useNavigate } from 'react-router-dom';
import { Upload, Loader2, Zap, Check } from 'lucide-react';
import InsuranceService from '../../services/InsuranceService';
import DocumentService from '../../services/DocumentService';
import { Card, Button, Input, InputGroup, FormSection, toast, MoneyInput } from '../../components/v2/V2Components';
import { sanitizeNumeric } from '../../utils/numericInput';
import usePageView from '../../hooks/usePageView';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';

const InsuranceForm = () => {
    const { houseId, policyId } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!policyId;

    usePageView(ANALYTICS_EVENTS.INSURANCE_FORM_VIEWED, { is_edit_mode: isEditMode });

    const [loading, setLoading] = useState(isEditMode);
    const [saving, setSaving] = useState(false);
    const [scanning, setScanning] = useState(false);

    const initialFormState = {
        id: null,
        insurer: '',
        policy_number: '',
        start_date: '',
        end_date: '',
        total_amount: '',
        deductible: '',
        wind_hail_deductible: '',
        coverage_limits: {
            dwelling: '',
            other_structures: '',
            personal_property: '',
            loss_of_use: '',
            personal_liability: '',
            medical_payments: ''
        },
        document_id: null,
        notes: ''
    };

    const [formData, setFormData] = useState(initialFormState);

    useEffect(() => {
        const fetchPolicy = async () => {
            try {
                const policy = await InsuranceService.getPolicy(policyId);
                setFormData({
                    id: policy.id,
                    insurer: policy.insurer || '',
                    policy_number: policy.policy_number || '',
                    start_date: policy.start_date || '',
                    end_date: policy.end_date || '',
                    total_amount: policy.total_amount || '',
                    deductible: policy.deductible || '',
                    wind_hail_deductible: policy.wind_hail_deductible || '',
                    coverage_limits: {
                        dwelling: policy.coverage_limits?.dwelling || '',
                        other_structures: policy.coverage_limits?.other_structures || '',
                        personal_property: policy.coverage_limits?.personal_property || '',
                        loss_of_use: policy.coverage_limits?.loss_of_use || '',
                        personal_liability: policy.coverage_limits?.personal_liability || '',
                        medical_payments: policy.coverage_limits?.medical_payments || ''
                    },
                    document_id: policy.document_id || null,
                    notes: policy.notes || ''
                });
            } catch (error) {
                toast.error?.('Failed to load insurance policy');
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        if (isEditMode) {
            fetchPolicy();
        }
    }, [houseId, policyId, isEditMode, navigate]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name.startsWith('coverage_')) {
            const field = name.replace('coverage_', '');
            setFormData(prev => ({
                ...prev,
                coverage_limits: { ...prev.coverage_limits, [field]: value }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    // Receives a single File from useFilePicker (the hook resets the
    // underlying input value so the same file can be re-selected).
    const handleScanDocument = async (file) => {
        if (!file) return;

        try {
            setScanning(true);
            toast.info?.('Scanning document...');

            // Step 1: Scan first — no side-effects if this fails
            const scanResponse = await InsuranceService.scanDocument(file);

            if (!scanResponse.success || !scanResponse.policy) {
                toast.error?.('Could not extract data from document.');
                return;
            }

            // Step 2: Upload only after scan succeeds — prevents orphaned Document rows
            const docResponse = await DocumentService.uploadDocument(file, {
                document_type: 'insurance',
                house_id: houseId,
                name: `Insurance Policy - ${file.name}`
            });
            const documentId = docResponse.id;

            const policy = scanResponse.policy;
            setFormData(prev => ({
                ...prev,
                insurer: policy.insurer || prev.insurer,
                policy_number: policy.policy_number || prev.policy_number,
                start_date: policy.start_date || prev.start_date,
                end_date: policy.end_date || prev.end_date,
                total_amount: policy.total_amount || prev.total_amount,
                deductible: policy.deductible || prev.deductible,
                wind_hail_deductible: policy.wind_hail_deductible || prev.wind_hail_deductible,
                coverage_limits: {
                    dwelling: policy.coverage_limits?.dwelling || prev.coverage_limits.dwelling,
                    other_structures: policy.coverage_limits?.other_structures || prev.coverage_limits.other_structures,
                    personal_property: policy.coverage_limits?.personal_property || prev.coverage_limits.personal_property,
                    loss_of_use: policy.coverage_limits?.loss_of_use || prev.coverage_limits.loss_of_use,
                    personal_liability: policy.coverage_limits?.personal_liability || prev.coverage_limits.personal_liability,
                    medical_payments: policy.coverage_limits?.medical_payments || prev.coverage_limits.medical_payments,
                },
                document_id: documentId,
            }));
            toast.success?.('Document scanned successfully! Please review the extracted data.');
        } catch (error) {
            toast.error?.(error.message || 'Error scanning document');
            console.error(error);
        } finally {
            setScanning(false);
            // useFilePicker resets the input's value automatically after onFiles fires.
        }
    };

    // OS-native chooser. See hooks/useFilePicker for the iOS rationale.
    const { triggerPicker: openScanPicker, fileInputElement: scanFileInput } = useFilePicker({
        kind: 'image-or-pdf',
        // Match the backend's 10MB per-file scan cap. Without this, an oversize
        // upload silently fails at the wire instead of getting an immediate toast.
        maxSizeMB: 10,
        onFiles: (files) => handleScanDocument(files[0]),
        disabled: scanning || saving,
    });

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            // sanitizeNumeric strips currency symbols and rejects malformed
            // input ("12-34", "1.2.3") instead of silently truncating like
            // bare parseFloat does. Shared across forms — see utils/numericInput.

            const payload = {
                ...formData,
                total_amount: sanitizeNumeric(formData.total_amount),
                deductible: sanitizeNumeric(formData.deductible),
                wind_hail_deductible: sanitizeNumeric(formData.wind_hail_deductible),
                coverage_limits: {
                    dwelling: sanitizeNumeric(formData.coverage_limits.dwelling),
                    other_structures: sanitizeNumeric(formData.coverage_limits.other_structures),
                    personal_property: sanitizeNumeric(formData.coverage_limits.personal_property),
                    loss_of_use: sanitizeNumeric(formData.coverage_limits.loss_of_use),
                    personal_liability: sanitizeNumeric(formData.coverage_limits.personal_liability),
                    medical_payments: sanitizeNumeric(formData.coverage_limits.medical_payments),
                }
            };

            if (isEditMode) {
                await InsuranceService.updatePolicy(formData.id, payload);
                toast.success?.('Policy updated');
            } else {
                await InsuranceService.createPolicy(houseId, payload);
                toast.success?.('Policy added');
            }

            navigate(`/houses/${houseId}?tab=insurance`);
        } catch (error) {
            toast.error?.(error.message || 'Failed to save policy');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return (
        <div className="flex justify-center items-center min-h-[60vh]">
            <Loader2 className="animate-spin text-[var(--primary)]" size={48} />
        </div>
    );

    return (
        <div className="animate-fade-in max-w-4xl mx-auto pb-10">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-[var(--text-main)] mb-1">
                        {isEditMode ? 'Edit Policy' : 'Add New Policy'}
                    </h1>
                    <p className="text-[var(--text-secondary)]">
                        {isEditMode ? 'Update your insurance policy details' : 'Add a new insurance policy for this property'}
                    </p>
                </div>
                <div className="flex space-x-3">
                    <button
                        type="button"
                        onClick={() => navigate(`/houses/${houseId}?tab=insurance`)}
                        className="px-4 py-2 border border-[var(--border)] text-[var(--text-secondary)] rounded-lg hover:bg-[var(--bg-hover)] transition-colors"
                        disabled={saving || scanning}
                    >
                        Cancel
                    </button>
                    {/* `form="insurance-form"` lets this header button trigger the form's
                        onSubmit handler so Enter-in-input also submits and HTML5 validation
                        on required fields fires before handleSave runs. */}
                    <button
                        type="submit"
                        form="insurance-form"
                        disabled={saving || scanning}
                        className="flex items-center space-x-2 px-6 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-all font-medium disabled:opacity-70"
                    >
                        {saving && <Loader2 size={16} className="animate-spin" />}
                        <span>{isEditMode ? 'Save Changes' : 'Create Policy'}</span>
                    </button>
                </div>
            </div>

            <div className="space-y-6">
                {/* Smart Autofill Card */}
                <Card className="p-6 border-l-4 border-l-[var(--primary)]">
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="text-lg font-semibold text-[var(--text-main)] flex items-center">
                                <Zap size={20} className="mr-2 text-[var(--primary)]" />
                                Smart Autofill
                            </h4>
                            <p className="text-sm text-[var(--text-secondary)] mt-1 max-w-lg">Upload your policy PDF or image to automatically extract the details. You can review and edit the data before saving.</p>
                        </div>
                        <div>
                            {scanFileInput}
                            <Button
                                variant="primary"
                                leadingIcon={scanning ? Loader2 : Upload}
                                onClick={openScanPicker}
                                disabled={scanning || saving}
                                className={scanning ? "animate-pulse" : ""}
                            >
                                {scanning ? "Scanning..." : "Scan Document"}
                            </Button>
                        </div>
                    </div>
                    {formData.document_id && (
                        <div className="mt-4 p-3 bg-[var(--bg-hover)] border border-[var(--border)] rounded text-[var(--text-main)] text-sm flex items-center">
                            <Check size={16} className="mr-2 text-[var(--primary)]" />
                            Document successfully uploaded and attached to this policy
                        </div>
                    )}
                </Card>

                {/* Form Content */}
                <Card className="p-8">
                    <form id="insurance-form" onSubmit={handleSave} className="space-y-8">
                        <FormSection title="Policy Information">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <InputGroup label="Insurance Company" required>
                                    <Input name="insurer" value={formData.insurer} onChange={handleInputChange} required />
                                </InputGroup>
                                <InputGroup label="Policy Number">
                                    <Input name="policy_number" value={formData.policy_number} onChange={handleInputChange} />
                                </InputGroup>
                                <InputGroup label="Start Date">
                                    <Input name="start_date" type="date" value={formData.start_date} onChange={handleInputChange} />
                                </InputGroup>
                                <InputGroup label="End Date">
                                    <Input name="end_date" type="date" value={formData.end_date} onChange={handleInputChange} />
                                </InputGroup>
                                <InputGroup label="Total Premium (Annual)">
                                    <MoneyInput name="total_amount" value={formData.total_amount} onChange={handleInputChange} />
                                </InputGroup>
                                <InputGroup label="Standard Deductible">
                                    <MoneyInput name="deductible" value={formData.deductible} onChange={handleInputChange} />
                                </InputGroup>
                                <InputGroup label="Wind/Hail Deductible">
                                    <MoneyInput name="wind_hail_deductible" value={formData.wind_hail_deductible} onChange={handleInputChange} />
                                </InputGroup>
                            </div>
                        </FormSection>

                        <FormSection title="Coverage Limits">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <InputGroup label="Dwelling">
                                    <MoneyInput name="coverage_dwelling" value={formData.coverage_limits.dwelling} onChange={handleInputChange} />
                                </InputGroup>
                                <InputGroup label="Other Structures">
                                    <MoneyInput name="coverage_other_structures" value={formData.coverage_limits.other_structures} onChange={handleInputChange} />
                                </InputGroup>
                                <InputGroup label="Personal Property">
                                    <MoneyInput name="coverage_personal_property" value={formData.coverage_limits.personal_property} onChange={handleInputChange} />
                                </InputGroup>
                                <InputGroup label="Loss of Use">
                                    <MoneyInput name="coverage_loss_of_use" value={formData.coverage_limits.loss_of_use} onChange={handleInputChange} />
                                </InputGroup>
                                <InputGroup label="Personal Liability">
                                    <MoneyInput name="coverage_personal_liability" value={formData.coverage_limits.personal_liability} onChange={handleInputChange} />
                                </InputGroup>
                                <InputGroup label="Medical Payments">
                                    <MoneyInput name="coverage_medical_payments" value={formData.coverage_limits.medical_payments} onChange={handleInputChange} />
                                </InputGroup>
                            </div>
                        </FormSection>
                    </form>
                </Card>
            </div>
        </div>
    );
};

export default InsuranceForm;
